import { useForm } from "react-hook-form";
import type { Error as Auth0Error } from "@auth0/auth0-acul-js";

import { butterfieldLogo } from "@/assets/logos";
import { useResetPasswordManager } from "../hooks/useResetPasswordManager";
import { getFieldError } from "../utils/helpers/errorUtils";
import {
  buildFallbackLoginLink,
  rebaseLinkToCurrentOrigin,
} from "../utils/helpers/urlUtils";

// const heroImageUrl =
//   "https://media.butterfield.com/page-image_imageUrl_a470c5db-2106-4964-a8f2-414c0415efe6_1744290400564.jpg";
const heroImageUrl =
  "https://cdn.mediavalet.com/cato/butterfield/aEeF6_P6lk6o6VqdMg_3cQ/GX_bog2pTkawcjy_quukQA/Large/DSC09355-slovenia-ivan-sardi-2026.jpeg?f=auto";

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
  captcha?: string;
}

function ResetPasswordScreen() {
  const {
    handleResetPassword,
    errors,
    texts,
    isCaptchaAvailable,
    captchaImage,
    loginLink,
    transactionState,
    prefilledIdentifier,
  } = useResetPasswordManager();

  const form = useForm<ResetPasswordFormData>({
    defaultValues: {
      password: "",
      confirmPassword: "",
      captcha: "",
    },
  });

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
  } = form;

  const sdkErrors = (errors || []) as Auth0Error[];
  const generalErrors = sdkErrors.filter((error) => !error.field);
  const passwordSDKError =
    getFieldError("password-reset", sdkErrors) ||
    getFieldError("password", sdkErrors);
  const confirmPasswordSDKError = getFieldError("re-enter-password", sdkErrors);
  const captchaSDKError = getFieldError("captcha", sdkErrors);
  const currentPassword = watch("password");

  const headingText = texts?.title || "Set a new password";
  const descriptionText =
    texts?.description ||
    "Create a new password to finish recovering your account.";
  const passwordLabel = (texts?.passwordPlaceholder || "New Password").concat("*");
  const confirmPasswordLabel = (
    texts?.passwordConfirmPlaceholder || "Re-enter Password"
  ).concat("*");
  const buttonText = texts?.buttonText || "Continue";
  const captchaLabel = (texts?.captchaCodePlaceholder || "CAPTCHA").concat("*");
  const backToLoginText =
    texts?.backToLoginText || texts?.backText || "Back to sign in";
  const localizedLoginLink = loginLink && rebaseLinkToCurrentOrigin(loginLink);
  const fallbackLoginLink = buildFallbackLoginLink(transactionState);
  const resolvedLoginLink = localizedLoginLink || fallbackLoginLink;

  const onSubmit = async (data: ResetPasswordFormData) => {
    await handleResetPassword(data.password, data.confirmPassword, data.captcha);
  };

  return (
    <div className="overflow-hidden rounded-none bg-white shadow-[0_30px_80px_rgba(2,6,23,0.35)] min-h-screen">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="relative order-1 h-56 sm:h-72 md:order-2 md:block md:h-full">
          <img
            src={heroImageUrl}
            alt="Auth0 experience preview"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/30" />
        </div>

        <form
          className="flex h-full flex-col justify-center gap-6 order-2 bg-white px-10 py-12 md:order-1 md:px-12"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-0 flex flex-col items-start text-left">
            <img
              src={butterfieldLogo}
              alt="Butterfield logo"
              className="h-14 w-auto"
              loading="lazy"
            />
            <p className="mt-4 font-display text-xl font-normal text-ink">{headingText}</p>
            <p className="mt-2 text-sm text-ink-2">{descriptionText}</p>
            {prefilledIdentifier && (
              <p className="mt-1 text-sm font-semibold text-ink">
                {prefilledIdentifier}
              </p>
            )}
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-none border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
              <ul className="list-disc space-y-1 pl-5">
                {generalErrors.map((error, index) => (
                  <li key={`resetpassword-general-error-${index}`}>{error.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <label
              className="text-sm font-semibold text-ink-2"
              htmlFor="password"
            >
              {passwordLabel}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder={texts?.passwordPlaceholder || "********"}
              className="input-field tracking-[0.3em]"
              aria-invalid={
                !!formErrors.password?.message || !!passwordSDKError || undefined
              }
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
                maxLength: {
                  value: 200,
                  message: "Maximum 200 characters allowed",
                },
              })}
            />
            {(formErrors.password?.message || passwordSDKError) && (
              <p className="text-sm text-danger">
                {formErrors.password?.message || passwordSDKError}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label
              className="text-sm font-semibold text-ink-2"
              htmlFor="confirm-password"
            >
              {confirmPasswordLabel}
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              placeholder={texts?.passwordPlaceholder || "********"}
              className="input-field tracking-[0.3em]"
              aria-invalid={
                !!formErrors.confirmPassword?.message ||
                !!confirmPasswordSDKError ||
                undefined
              }
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === currentPassword || "Passwords do not match",
              })}
            />
            {(formErrors.confirmPassword?.message || confirmPasswordSDKError) && (
              <p className="text-sm text-danger">
                {formErrors.confirmPassword?.message || confirmPasswordSDKError}
              </p>
            )}
          </div>

          {isCaptchaAvailable && (
            <div className="space-y-3">
              <label
                htmlFor="captcha"
                className="text-sm font-semibold text-ink-2"
              >
                {captchaLabel}
              </label>
              {captchaImage && (
                <img
                  src={captchaImage}
                  alt="CAPTCHA challenge"
                  className="h-16 w-full rounded-none border border-border object-contain"
                />
              )}
              <input
                id="captcha"
                type="text"
                placeholder={texts?.captchaCodePlaceholder || "Enter the code"}
                className="input-field"
                aria-invalid={
                  !!formErrors.captcha?.message || !!captchaSDKError || undefined
                }
                {...register("captcha", {
                  required: "Please complete the CAPTCHA",
                  maxLength: {
                    value: 15,
                    message: "CAPTCHA code is too long",
                  },
                })}
              />
              {(formErrors.captcha?.message || captchaSDKError) && (
                <p className="text-sm text-danger">
                  {formErrors.captcha?.message || captchaSDKError}
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-sm border border-accent bg-accent px-4 py-3 text-base font-semibold uppercase tracking-[0.1em] text-on-accent transition hover:cursor-pointer hover:border-accent-hover hover:bg-accent-hover hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Please wait..." : buttonText}
          </button>

          {resolvedLoginLink ? (
            <a
              href={resolvedLoginLink}
              className="text-sm font-semibold text-ink-2 underline underline-offset-2"
            >
              {backToLoginText}
            </a>
          ) : (
            <p className="text-sm font-semibold text-ink-2">{backToLoginText}</p>
          )}
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordScreen;
